import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  Platform,
  Dimensions,
  KeyboardAvoidingView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as GoogleSignIn from 'expo-auth-session/providers/google';
import * as AppleAuthentication from 'expo-apple-authentication';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

const RegisterScreen = ({ navigation, route }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [isAppleSignInAvailable, setIsAppleSignInAvailable] = useState(false);

  const userType = route?.params?.userType || 'professional';

  // Check Apple Sign In availability
  useEffect(() => {
    const checkAppleSignIn = async () => {
      if (Platform.OS === 'ios') {
        try {
          const isAvailable = await AppleAuthentication.isAvailableAsync();
          setIsAppleSignInAvailable(isAvailable);
        } catch (error) {
          console.log('Apple Sign In not available');
        }
      }
    };
    checkAppleSignIn();
  }, []);

  // Google Sign In configuration
  const [request, response, promptAsync] = GoogleSignIn.useAuthRequest({
    clientId: Platform.select({
      ios: 'YOUR_IOS_CLIENT_ID.apps.googleusercontent.com',
      android: 'YOUR_ANDROID_CLIENT_ID.apps.googleusercontent.com',
      default: 'YOUR_WEB_CLIENT_ID.apps.googleusercontent.com',
    }),
  });

  // Handle Google Sign In response
  useEffect(() => {
    if (response?.type === 'success') {
      handleGoogleSignUp(response);
    }
  }, [response]);

  const handleGoogleSignUp = async (authResponse) => {
    try {
      setGoogleLoading(true);
      
      const userInfoResponse = await fetch(
        `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${authResponse.authentication.accessToken}`
      );
      const userInfo = await userInfoResponse.json();

      const serverResponse = await fetch('https://api.jobeng.ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: authResponse.authentication.accessToken,
          user_info: userInfo,
          user_type: userType,
          is_signup: true,
        }),
      });

      const result = await serverResponse.json();

      if (result.success) {
        await AsyncStorage.setItem('userToken', result.data.token);
        await AsyncStorage.setItem('userData', JSON.stringify(result.data.user));

        Alert.alert('Success', 'Account created successfully with Google!', [
          {
            text: 'OK',
            onPress: () => navigateToWelcome(result.data.user),
          },
        ]);
      } else {
        Alert.alert('Error', result.error || 'Google signup failed');
      }
    } catch (error) {
      console.error('Google signup error:', error);
      Alert.alert('Error', 'Failed to sign up with Google. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleSignUp = async () => {
    try {
      setAppleLoading(true);
      
      const credential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
      });

      const serverResponse = await fetch('https://api.jobeng.ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identity_token: credential.identityToken,
          authorization_code: credential.authorizationCode,
          email: credential.email,
          full_name: credential.fullName ? 
            `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim() : 
            'Apple User',
          user_type: userType,
          is_signup: true,
        }),
      });

      const result = await serverResponse.json();

      if (result.success) {
        await AsyncStorage.setItem('userToken', result.data.token);
        await AsyncStorage.setItem('userData', JSON.stringify(result.data.user));

        Alert.alert('Success', 'Account created successfully with Apple!', [
          {
            text: 'OK',
            onPress: () => navigateToWelcome(result.data.user),
          },
        ]);
      } else {
        Alert.alert('Error', result.error || 'Apple signup failed');
      }
    } catch (error) {
      console.error('Apple signup error:', error);
      if (error.code !== 'ERR_CANCELED') {
        Alert.alert('Error', 'Failed to sign up with Apple. Please try again.');
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const handleEmailSignUp = () => {
    switch (userType) {
      case 'business':
        navigation.navigate('BusinessSignup');
        break;
      case 'professional':
        navigation.navigate('ProfessionalSignup');
        break;
      case 'learning':
        navigation.navigate('LearningSignup');
        break;
      default:
        navigation.navigate('ProfessionalSignup');
    }
  };

  const navigateToWelcome = (user) => {
    switch (user.user_type) {
      case 'business':
        navigation.navigate('WelcomeBusiness');
        break;
      case 'professional':
        navigation.navigate('WelcomeProfessionalScreen');
        break;
      case 'learning':
        navigation.navigate('WelcomeLearningScreen');
        break;
      default:
        navigation.navigate('WelcomeProfessionalScreen');
    }
  };

  const getUserTypeInfo = () => {
    switch (userType) {
      case 'business':
        return {
          title: 'Business Registration',
          subtitle: 'Create your business account',
          icon: 'business',
          color: '#007AFF',
        };
      case 'professional':
        return {
          title: 'Professional Registration',
          subtitle: 'Join the professional network',
          icon: 'person',
          color: '#34C759',
        };
      case 'learning':
        return {
          title: 'Learning Registration',
          subtitle: 'Start your learning journey',
          icon: 'school',
          color: '#FF9500',
        };
      default:
        return {
          title: 'Registration',
          subtitle: 'Create your account',
          icon: 'person-add',
          color: '#007AFF',
        };
    }
  };

  const userTypeInfo = getUserTypeInfo();

  return (
    <SafeAreaView style={styles.safeArea}>
      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
          bounces={true}
          alwaysBounceVertical={true}
          scrollEventThrottle={16}
        >
          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity 
              style={styles.backButton} 
              onPress={() => navigation.goBack()}
            >
              <Ionicons name="arrow-back" size={24} color="#333" />
            </TouchableOpacity>
            
            <View style={styles.logoContainer}>
              <Text style={styles.logoText}>JobEng</Text>
              <Text style={styles.logoSubtext}>UK Engineering Excellence</Text>
            </View>
          </View>

          {/* User Type Section */}
          <View style={styles.userTypeSection}>
            <View style={[styles.userTypeIcon, { backgroundColor: userTypeInfo.color }]}>
              <Ionicons name={userTypeInfo.icon} size={40} color="white" />
            </View>
            <Text style={styles.userTypeTitle}>{userTypeInfo.title}</Text>
            <Text style={styles.userTypeSubtitle}>{userTypeInfo.subtitle}</Text>
          </View>

          {/* Registration Options */}
          <View style={styles.registrationSection}>
            <Text style={styles.sectionTitle}>Choose your signup method</Text>

            {/* Google Sign Up Button */}
            <TouchableOpacity
              style={[styles.socialButton, styles.googleButton]}
              onPress={() => promptAsync()}
              disabled={!request || googleLoading}
            >
              {googleLoading ? (
                <ActivityIndicator color="white" size={20} />
              ) : (
                <>
                  <Ionicons name="logo-google" size={24} color="white" />
                  <Text style={styles.socialButtonText}>Continue with Google</Text>
                </>
              )}
            </TouchableOpacity>

            {/* Apple Sign Up Button (iOS only) */}
            {isAppleSignInAvailable && (
              <TouchableOpacity
                style={[styles.socialButton, styles.appleButton]}
                onPress={handleAppleSignUp}
                disabled={appleLoading}
              >
                {appleLoading ? (
                  <ActivityIndicator color="white" size={20} />
                ) : (
                  <>
                    <Ionicons name="logo-apple" size={24} color="white" />
                    <Text style={styles.socialButtonText}>Continue with Apple</Text>
                  </>
                )}
              </TouchableOpacity>
            )}

            {/* Divider */}
            <View style={styles.divider}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>Or</Text>
              <View style={styles.dividerLine} />
            </View>

            {/* Choose an account type to sign up */}
            <TouchableOpacity
              style={styles.emailButton}
              onPress={() => navigation.navigate('ProfessionalSignup')}
              disabled={isLoading}
            >
              <Ionicons name="person" size={24} color="white" />
              <Text style={styles.emailButtonText}>Sign up as a Professional</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.emailButton, { marginTop: 12 }]}
              onPress={() => navigation.navigate('BusinessSignup')}
              disabled={isLoading}
            >
              <Ionicons name="business" size={24} color="white" />
              <Text style={styles.emailButtonText}>Sign up as a Business</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.emailButton, { marginTop: 12 }]}
              onPress={() => navigation.navigate('LearningSignup')}
              disabled={isLoading}
            >
              <Ionicons name="school" size={24} color="white" />
              <Text style={styles.emailButtonText}>Sign up to Learn</Text>
            </TouchableOpacity>
          </View>

          {/* Features Section */}
          <View style={styles.featuresSection}>
            <Text style={styles.featuresTitle}>What you'll get with JobEng:</Text>
            
            <View style={styles.featureItem}>
              <Ionicons name="checkmark-circle" size={24} color={userTypeInfo.color} />
              <Text style={styles.featureText}>Professional networking opportunities</Text>
            </View>
            
            <View style={styles.featureItem}>
              <Ionicons name="checkmark-circle" size={24} color={userTypeInfo.color} />
              <Text style={styles.featureText}>Access to exclusive job opportunities</Text>
            </View>
            
            <View style={styles.featureItem}>
              <Ionicons name="checkmark-circle" size={24} color={userTypeInfo.color} />
              <Text style={styles.featureText}>Industry insights and trends</Text>
            </View>
            
            <View style={styles.featureItem}>
              <Ionicons name="checkmark-circle" size={24} color={userTypeInfo.color} />
              <Text style={styles.featureText}>Skill development resources</Text>
            </View>
            
            <View style={styles.featureItem}>
              <Ionicons name="checkmark-circle" size={24} color={userTypeInfo.color} />
              <Text style={styles.featureText}>Career advancement tools</Text>
            </View>
            
            <View style={styles.featureItem}>
              <Ionicons name="checkmark-circle" size={24} color={userTypeInfo.color} />
              <Text style={styles.featureText}>Secure and private platform</Text>
            </View>
          </View>

          {/* Info Section */}
          <View style={styles.infoSection}>
            <Text style={styles.infoTitle}>Why choose JobEng?</Text>
            <Text style={styles.infoText}>
              JobEng is the UK's premier platform for engineering professionals. 
              Connect with top employers, access exclusive opportunities, and 
              advance your career in the engineering industry.
            </Text>
            
            <Text style={styles.infoText}>
              Our platform is trusted by thousands of engineers and hundreds 
              of companies across the United Kingdom.
            </Text>
          </View>

          {/* Stats Section */}
          <View style={styles.statsSection}>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>10,000+</Text>
              <Text style={styles.statLabel}>Engineers</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>500+</Text>
              <Text style={styles.statLabel}>Companies</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>1,000+</Text>
              <Text style={styles.statLabel}>Jobs Posted</Text>
            </View>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>
              Already have an account?{' '}
              <Text 
                style={[styles.footerLink, { color: userTypeInfo.color }]}
                onPress={() => navigation.navigate('LoginScreen', { userType })}
              >
                Sign In
              </Text>
            </Text>
            
            <Text style={styles.termsText}>
              By signing up, you agree to our Terms of Service and Privacy Policy
            </Text>
          </View>

          {/* Extra space to ensure scrolling */}
          <View style={styles.extraSpace} />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100,
    minHeight: screenHeight + 500, // Force content to be taller than screen
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 30,
  },
  backButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'white',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  logoContainer: {
    flex: 1,
    alignItems: 'center',
    marginLeft: -44,
  },
  logoText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
  },
  logoSubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  userTypeSection: {
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 40,
  },
  userTypeIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  userTypeTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 8,
  },
  userTypeSubtitle: {
    fontSize: 18,
    color: '#666',
    textAlign: 'center',
  },
  registrationSection: {
    paddingHorizontal: 20,
    paddingVertical: 40,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
    marginBottom: 30,
  },
  socialButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 18,
    paddingHorizontal: 20,
    borderRadius: 12,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  googleButton: {
    backgroundColor: '#DB4437',
  },
  appleButton: {
    backgroundColor: '#000',
  },
  socialButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 12,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 25,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#E0E0E0',
  },
  dividerText: {
    marginHorizontal: 20,
    color: '#666',
    fontSize: 16,
  },
  emailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#007AFF',
    paddingVertical: 18,
    paddingHorizontal: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  emailButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 12,
  },
  featuresSection: {
    paddingHorizontal: 20,
    paddingVertical: 40,
  },
  featuresTitle: {
    fontSize: 22,
    fontWeight: '600',
    color: '#333',
    marginBottom: 25,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 5,
  },
  featureText: {
    fontSize: 16,
    color: '#666',
    marginLeft: 12,
    flex: 1,
  },
  infoSection: {
    paddingHorizontal: 20,
    paddingVertical: 40,
  },
  infoTitle: {
    fontSize: 22,
    fontWeight: '600',
    color: '#333',
    marginBottom: 15,
  },
  infoText: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
    marginBottom: 15,
  },
  statsSection: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingHorizontal: 20,
    paddingVertical: 40,
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#007AFF',
  },
  statLabel: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  footer: {
    paddingHorizontal: 20,
    alignItems: 'center',
    paddingVertical: 40,
  },
  footerText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 15,
  },
  footerLink: {
    fontWeight: '600',
  },
  termsText: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    lineHeight: 20,
  },
  extraSpace: {
    height: 100,
  },
});

export default RegisterScreen;
